/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 *
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @version 1.26.0
 * @since 1.25.0
 */

import { useCallback, useEffect, useMemo } from 'react';

import { MenuWrapper, Typography } from '@nexign/scc-ui-kit';
import { TNotificationsHeaderProps } from '@nexign/scc-ui-kit/lib/components/Notifications/NotificationsHeader';
import isFunction from 'lodash/isFunction';
import { useSelector } from 'react-redux';

import { icons } from '@/main/assets';
import { ImgStyled } from '@/main/components/common/Block/Img';
import { appHistoryPush } from '@/main/modules/app/appHistory';
import { makeSelectUserNotificationsTranslation } from '@/main/modules/internationalization/intSelectors';
import { RoutePath } from '@/main/routes/constants/routesConstants';
import ServiceUserDenied from '@modules/role/components/ServiceUserDenied';

import { ENotificationUpdateStatus } from '../constants';
import { useUpdateNotificationMutation, useDeleteNotificationMutation } from '../store';
import {
    StyledNotificationsButtonLink,
    StyledNotificationsHeaderMenuContainer,
    StyledNotificationsHeaderMenuItem,
} from '../styled/UserNotificationsStyled';

export const useHeaderParams = (
    disableHeaderMenu: boolean,
    handleDisableControls: (disable: boolean) => void,
    handleNotificationsClose?: () => void,
): TNotificationsHeaderProps => {
    const notificationTranslation = useSelector(makeSelectUserNotificationsTranslation());

    const { header: tHeader } = notificationTranslation;

    const [deleteNotification, { isLoading: deleteIsLoading }] = useDeleteNotificationMutation();
    const [updateNotification, { isLoading: updateAllIsLoading }] = useUpdateNotificationMutation();

    const handleUnviewAllNotifications = useCallback(() => {
        updateNotification({ changeAll: true, status: ENotificationUpdateStatus.UNVIEWED });
    }, [updateNotification]);

    const handleViewAllNotifications = useCallback(() => {
        updateNotification({ changeAll: true, status: ENotificationUpdateStatus.VIEWED });
    }, [updateNotification]);

    const handleDeleteAllNotifications = useCallback(() => {
        deleteNotification({ removeAll: true });
    }, [deleteNotification]);

    useEffect(() => {
        handleDisableControls(updateAllIsLoading || deleteIsLoading);
    }, [deleteIsLoading, handleDisableControls, updateAllIsLoading]);

    const handleSettingsButtonClick = useCallback(() => {
        appHistoryPush(RoutePath.settingsNotifications);
        if (isFunction(handleNotificationsClose)) {
            handleNotificationsClose();
        }
    }, [handleNotificationsClose]);

    const menuItems = useMemo(
        () => [
            {
                text: tHeader.menuItems.viewAll,
                icon: <ImgStyled className="medium" src={icons.checkThinIcon} />,
                onClick: handleViewAllNotifications,
            },
            {
                text: tHeader.menuItems.unViewAll,

                icon: <ImgStyled className="medium" src={icons.checkThinIcon} />,
                onClick: handleUnviewAllNotifications,
            },

            {
                text: tHeader.menuItems.deleteAll,
                icon: <ImgStyled className="medium" src={icons.deleteDark} />,
                onClick: handleDeleteAllNotifications,
            },
        ],
        [
            handleDeleteAllNotifications,
            handleUnviewAllNotifications,
            handleViewAllNotifications,
            tHeader.menuItems.deleteAll,
            tHeader.menuItems.unViewAll,
            tHeader.menuItems.viewAll,
        ],
    );

    return useMemo(() => {
        const StartComponent = (
            <StyledNotificationsButtonLink
                color="black"
                size="small"
                startIcon={<ImgStyled className="small" src={icons.settingIcon} />}
                typographyProps={{ variant: 'h5' }}
                variant="text"
                onClick={handleSettingsButtonClick}
            >
                {tHeader.startLink}
            </StyledNotificationsButtonLink>
        );

        const EndComponent = disableHeaderMenu ? undefined : (
            <StyledNotificationsHeaderMenuContainer>
                <MenuWrapper
                    withIcon
                    disablePortal={false}
                    placement="bottom-end"
                    style={{ zIndex: 502 }}
                    title={tHeader.menuTitle}
                >
                    {menuItems.map((el, index) => (
                        <ServiceUserDenied
                            renderComponent={(disabled) => (
                                <StyledNotificationsHeaderMenuItem
                                    disabled={disabled}
                                    key={`${index}-${el.text}`}
                                    onClick={el.onClick}
                                >
                                    {el.icon}
                                    <Typography>{el.text}</Typography>
                                </StyledNotificationsHeaderMenuItem>
                            )}
                        />
                    ))}
                </MenuWrapper>
            </StyledNotificationsHeaderMenuContainer>
        );

        return {
            StartComponent,
            EndComponent,
        };
    }, [disableHeaderMenu, handleSettingsButtonClick, menuItems, tHeader.menuTitle, tHeader.startLink]);
};
